import { StoreLinks } from './storeLinks.mjs'

/**
 * `https://<city domain>/go/<source>` — one link per channel (Threads,
 * Instagram, YouTube, the site itself …) that opens the app when it is
 * installed and otherwise sends the visitor where they can get it
 * (TASK-1076, spec `docs/superpowers/specs/2026-10-18-TASK-1076-metro-tracking-links-design.md`).
 *
 * The redirect itself is nginx (`infra/nginx/london-tube.com.conf`,
 * `location ~ ^/go/…`), so a new source needs no deploy: any slug matching
 * {@link SOURCE_PATTERN} works the moment someone posts it. This file is the
 * grammar both sides agree on; `src/deploy/trackingLinks.test.ts` pins the
 * nginx targets to it byte for byte.
 *
 * The apps claim `/go/*` too (AASA here, App Links on Android) and report
 * `deeplink_opened {type: "tracking_link", source}` — the same slug rules
 * live in their `TrackingLinkParser`.
 */

/**
 * Slug of a source. `go-` + 27 characters = 30, the App Store Connect limit
 * for a campaign token; lower case only, so `Threads` and `threads` can never
 * become two campaigns.
 */
const SOURCE_PATTERN = '[a-z0-9-]{1,27}'

const SOURCE_REGEX = new RegExp(`^${SOURCE_PATTERN}$`)

/** `utm_medium` of every tracking link — tells them apart from the site's own `web` slots. */
const MEDIUM = 'go'

/** @param {string} source */
function campaign(source) {
  return `go-${source}`
}

/** Prefix of the site's own sources — `wiki/02-process/tracking-links.md` reserves `site-*`. */
const SITE_PREFIX = 'site-'

/**
 * Source of one of the site's own store CTAs (TASK-1170): the slot
 * `web-<surface>` becomes `site-<surface>`, so the site is one more channel
 * in the link report and its slugs can never collide with a posted one.
 * Only a registered slot is accepted — a typo throws instead of becoming a
 * link that attributes to nobody.
 *
 * @param {string} slot one of `StoreLinks.SLOTS`
 */
function siteSource(slot) {
  if (!Object.values(StoreLinks.SLOTS).includes(slot)) {
    throw new Error(`siteSource expects one of StoreLinks.SLOTS, got: ${slot}`)
  }
  const source = `${SITE_PREFIX}${slot.replace(/^web-/, '')}`
  if (!SOURCE_REGEX.test(source)) {
    throw new Error(`siteSource built an invalid source: ${source}`)
  }
  return source
}

export const TrackingLinks = Object.freeze({
  SOURCE_PATTERN,
  MEDIUM,

  /** @param {string} source */
  isValidSource(source) {
    return SOURCE_REGEX.test(source)
  },

  /**
   * ASC `ct` and `utm_campaign` of a source. Prefixed so tracking-link
   * campaigns never collide with the site's `web-*` store slots.
   *
   * @param {string} source
   */
  campaign,

  siteSource,

  /**
   * The public link. `domain` is the city's (`CITY.domain`); London by
   * default, the domain every link before TASK-1170 was posted on. Throws on
   * a source nginx would not match — such a link silently lands on home.
   *
   * @param {string} source
   * @param {string} [domain]
   */
  url(source, domain = 'london-tube.com') {
    if (!SOURCE_REGEX.test(source)) {
      throw new Error(`invalid tracking-link source: ${source}`)
    }
    return `https://${domain}/go/${source}`
  },

  /**
   * Where nginx sends a visitor who reached the web (the app did not open).
   *
   * @param {string} source
   * @param {string} [locale] `$best_locale` of the request
   */
  targets(source, locale = 'en') {
    const ct = campaign(source)
    return {
      ios: StoreLinks.appStore(ct),
      android: StoreLinks.googlePlayReferred({ source, medium: MEDIUM, campaign: ct }),
      web: `https://london-tube.com/${locale}?utm_source=${source}&utm_medium=${MEDIUM}&utm_campaign=${ct}`,
    }
  },
})
